/**
 * `isSafeHttpUrl` — read-side URL-scheme guard for member content surfaces
 * (029 review finding, defense-in-depth).
 *
 * The member cards render authored snapshot URLs into navigation sinks: the
 * benefit card's external `<a href>` ("Lue lisää täältä") and the video card's
 * `window.open(videoUrl)`. The authoring path validates URL schemes on write
 * (`benefitClientSchema` / `videoListingClientSchema` reject non-http(s)), but
 * the read boundary must not TRUST that — a `javascript:` or `data:` URL that
 * reached a published snapshot (legacy row, direct DB write, a future authoring
 * regression) must never reach the sink. This mirrors the read-side DOMPurify
 * posture (`SanitizedHtml`): sanitise at render, do not assume clean input.
 *
 * Scope: this guards NAVIGATION sinks — `<a href>` and `window.open` — where a
 * `javascript:` URL executes script. It is deliberately NOT applied to `<img src>`
 * (`logoUrl` / `thumbnailUrl`): an image `src` is not a script-execution sink (a
 * `javascript:`/`data:` `src` does not run JS in modern browsers), and those fields
 * legitimately carry same-origin relative `/media/<uuid>.png` paths that this
 * absolute-http(s)-only predicate would wrongly reject. Media URLs are validated
 * write-side by `mediaAssetUrlSchema`.
 *
 * Returns `true` ONLY for `http:` / `https:` URLs. Everything else — an
 * unparseable string, a `javascript:` / `data:` / `mailto:` scheme, a relative
 * path (no base), or the empty string — returns `false`, so the caller treats
 * it as "no usable URL" (the anchor is not rendered, Play is disabled).
 */
export function isSafeHttpUrl(url: string): boolean {
  if (url.length === 0) return false;
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    // `new URL` throws on an unparseable / base-less relative input: not a
    // navigable absolute URL, so it is not safe to hand to a navigation sink.
    return false;
  }
}
